import { useSupplyUiConfig } from '../invoice-fields'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CalendarOutlined, FileSearchOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons'
import { Alert, Button, Form, Result, Skeleton, Space, Typography } from 'antd'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import fetchAPI from '../../../../lib/utils/fetch-api'
import DataTable from '../../../components/data-table'
import FiltersList from '../../../components/v2/filters'
import { filtersType } from '../../../components/v2/filters/interfaces'
import FiltersDrawer, { FiltersDrawerRef } from '../../../components/v2/filters-drawer'
import {
  getColumnOrder,
  getJsonSettingsByKey,
  TableSettingsButton,
  TransferItem,
} from '../../../components/v2/table-settings-button'
import { useHeaderActions } from '../../../contexts/header-actions-context'
import B2BLayout from '../../../layouts/b2b'
import StickyToolbar from '../../../layouts/b2b/sticky-toolbar'
import { getSupplyColumns } from '../columns'
import {
  SupplyDataset,
  SupplyRow,
  supplyCardPath,
  supplyDate,
  supplyDeliveryDate,
  supplyDeliveryPeriod,
  supplyMoney,
  supplySections,
  supplyText,
} from '../model'

type SupplyListRow = SupplyRow & { id: string }

const SupplyList: React.FC<{ dataset: SupplyDataset }> = ({ dataset }) => {
  const { pathname, search } = useLocation()
  const { can_open_chat: canOpenChat } = useSupplyUiConfig()
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const toolbarRef = useRef<HTMLDivElement | null>(null)
  const filtersDrawerRef = useRef<FiltersDrawerRef | null>(null)
  const { setHeaderActions } = useHeaderActions()
  const [rows, setRows] = useState<SupplyListRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)
  const [complete, setComplete] = useState(true)
  const section = supplySections[dataset]
  const query = useMemo(() => new URLSearchParams(search), [search])
  const number = query.get('number')?.trim() || ''
  const supplier = query.get('supplier')?.trim() || ''
  const deliveryDate = dataset === 'receipts' ? '' : query.get('delivery_date') || ''
  const deliveryPeriod = useMemo(() => supplyDeliveryPeriod(deliveryDate), [deliveryDate])
  const page = Math.max(1, Number(query.get('page')) || 1)
  const pageSize = Math.min(100, Math.max(10, Number(query.get('pageSize')) || 50))
  const jsonSettings = useSelector(state => getJsonSettingsByKey(state, pathname))

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    if (deliveryDate && !deliveryPeriod) {
      setError('Укажите корректный период плановой доставки')
      setLoading(false)
      return
    }
    const params = new URLSearchParams({ limit: deliveryPeriod ? '2000' : '100' })
    if (deliveryPeriod) {
      params.set('date_field', 'delivery')
      params.set('date_from', deliveryPeriod.from)
      params.set('date_to', deliveryPeriod.to)
    }
    fetchAPI(`/api/supply/${dataset}?${params}`, { timeoutMs: 85000 })
      .then(result => {
        if (!active) return
        if (!Array.isArray(result.data)) throw new Error('Источник 1С вернул некорректный список')
        setComplete(result.complete !== false)
        setRows(
          result.data.map((row: SupplyRow, index: number) => ({
            ...row,
            id: `${row.Номер}-${row.Дата}-${index}`,
          })),
        )
      })
      .catch(cause => {
        if (active) setError(cause.message || 'Не удалось загрузить данные 1С')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [dataset, refreshKey, deliveryDate, deliveryPeriod])

  useEffect(() => {
    setHeaderActions([
      {
        key: 'filters',
        icon: <FilterOutlined />,
        onClick: () => filtersDrawerRef.current?.toggle(),
        tooltip: 'Фильтры',
      },
    ])
    return () => setHeaderActions([])
  }, [setHeaderActions])

  const filters = useMemo<filtersType[]>(
    () => [
      {
        name: 'number',
        label: 'Номер',
        type: 'input-string',
        placeholder: section.searchPlaceholder,
        value: number,
        style: { width: 260 },
      },
      {
        name: 'supplier',
        label: 'Поставщик',
        type: 'input-string',
        value: supplier,
        style: { width: 230 },
      },
      ...(dataset !== 'receipts' ? [{
        name: 'delivery_date',
        label: 'Плановая дата доставки',
        type: 'dates' as const,
        value: deliveryDate,
        placeholder: ['Доставка с', 'Доставка по'],
        options: { tooltip: 'Плановая дата доставки по данным 1С' },
        style: { width: 280 },
      }] : []),
    ],
    [section.searchPlaceholder, number, supplier, dataset, deliveryDate],
  )

  const changeFilters = useCallback(
    (values: Record<string, string | undefined>) => {
      const next = new URLSearchParams(search)
      for (const key of ['number', 'supplier', 'delivery_date']) {
        const value = values[key]?.trim()
        if (value) next.set(key, value)
        else next.delete(key)
      }
      next.delete('page')
      navigate({ search: next.toString() })
    },
    [navigate, search],
  )

  const filteredRows = useMemo(
    () =>
      rows.filter(row => {
        const matchesNumber =
          !number ||
          [row.Номер, row.Название].some(value =>
            String(value ?? '')
              .toLocaleLowerCase('ru-RU')
              .includes(number.toLocaleLowerCase('ru-RU')),
          )
        const matchesSupplier =
          !supplier ||
          String(row.Поставщик ?? '')
            .toLocaleLowerCase('ru-RU')
            .includes(supplier.toLocaleLowerCase('ru-RU'))
        return matchesNumber && matchesSupplier
      }),
    [rows, number, supplier],
  )

  const columns = useMemo(() => getSupplyColumns(dataset), [dataset])
  const viewColumns = useMemo(() => {
    const order = getColumnOrder(jsonSettings)
    if (!order.length) return columns
    const ordered = order
      .map(key => columns.find(column => column.dataIndex === key))
      .filter((column): column is (typeof columns)[number] => Boolean(column))
    return ordered.length ? ordered : columns
  }, [columns, jsonSettings])
  const settingsColumns = useMemo<TransferItem[]>(
    () => columns.map(column => ({ key: String(column.dataIndex), title: String(column.title) })),
    [columns],
  )
  const mobileFields = useMemo(
    () => [
      { key: 'Номер', label: '', render: supplyText },
      { key: 'Дата', label: 'Дата', render: supplyDate },
      ...(dataset !== 'receipts' ? [{ key: 'ПлановаяДатаДоставки', label: 'Плановая дата доставки', render: supplyDeliveryDate }] : []),
      { key: 'Поставщик', label: 'Поставщик', render: supplyText },
      { key: 'Сумма', label: 'Сумма', render: supplyMoney },
    ],
    [dataset],
  )
  const currentPage = Math.min(page, Math.max(1, Math.ceil(filteredRows.length / pageSize)))

  const changePage = (nextPage: number, nextPageSize: number) => {
    const next = new URLSearchParams(search)
    if (nextPage > 1) next.set('page', String(nextPage))
    else next.delete('page')
    if (nextPageSize !== 50) next.set('pageSize', String(nextPageSize))
    else next.delete('pageSize')
    navigate({ search: next.toString() })
  }

  return (
    <B2BLayout
      breadcrumbs={[{ href: section.path, title: section.title }]}
      pageTitle={section.title}
    >
      {loading && <Skeleton active />}
      {!loading && error && <Result status="error" title="Ошибка загрузки" subTitle={error} />}
      {!loading && !error && (
        <>
          <StickyToolbar
            ref={toolbarRef}
            collapsible={
              <Space wrap align="start">
                <FiltersList
                  filters={filters.filter(filter => filter.name !== 'supplier')}
                  onChange={values => changeFilters({ number, supplier, delivery_date: deliveryDate, ...values })}
                  form={form}
                />
                <Button icon={<CalendarOutlined />} onClick={() => navigate('/warehouse/deliveries')}>Календарь поставок</Button>
                <Button icon={<ReloadOutlined />} onClick={() => setRefreshKey(key => key + 1)}>
                  Обновить
                </Button>
                {dataset === 'receipts' && <Button type="primary" icon={<FileSearchOutlined />} onClick={() => navigate(canOpenChat ? '/warehouse/reviews' : '/warehouse/incoming')}>
                  Разобрать входящие документы
                </Button>}
                <FiltersDrawer
                  ref={filtersDrawerRef}
                  filters={filters}
                  currentFilters={{ number: number || undefined, supplier: supplier || undefined, delivery_date: deliveryDate || undefined }}
                  onChange={changeFilters}
                />
              </Space>
            }
            persistent={
              <Space wrap align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
                <Typography.Text type="secondary">
                  Данные из 1С · только просмотр · {deliveryPeriod ? 'по плановой дате доставки' : 'последние 100 документов'}
                </Typography.Text>
                <Space wrap>
                  {dataset === 'receipts' && <Button onClick={() => navigate('/warehouse/incoming')}>Входящие накладные</Button>}
                  <TableSettingsButton items={settingsColumns} />
                </Space>
              </Space>
            }
          />
          {deliveryPeriod && !complete && <Alert type="warning" showIcon message="Показаны первые 2000 документов за выбранный период доставки. Сузьте период, чтобы увидеть полный список." style={{ marginBottom: 12 }} />}
          {number && filteredRows.length === 0 && (
            <Button onClick={() => navigate(supplyCardPath(dataset, number))}>
              Открыть документ по точному номеру
            </Button>
          )}
          <DataTable
            columns={viewColumns as any}
            mobileFields={mobileFields}
            data={filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
            rowKey="id"
            pagination={{ current: currentPage, pageSize, total: filteredRows.length }}
            onPageChange={changePage}
            onRowClick={row => navigate(supplyCardPath(dataset, String(row.Номер)))}
            headerRef={toolbarRef}
            bordered
            maxLines={3}
          />
        </>
      )}
    </B2BLayout>
  )
}

export const SupplyRequestsPage = () => <SupplyList dataset="requests" />
export const ProcurementPage = () => <SupplyList dataset="orders" />
export const WarehousePage = () => <SupplyList dataset="receipts" />
